import React, { useEffect } from 'react';
import { useStore } from '../../context/StoreContext';
import buildFooterHtml, { FOOTER_SHOP_LINKS, initialsOf } from '../../pages/StoreBuilder/footerMarkup';
import { FOOTER_STYLES } from '../../pages/StoreBuilder/footerStyles';

/* The storefront footer, drawn from the store's own settings by the same builder
   the Store designer uses. It is built here rather than filled from the published
   html_content, because `buildHtmlExport()` strips `<footer>…</footer>` from the
   export — no storefront's saved markup has a footer to fill.

   Two deliberate differences from the designer, both because it only draws the
   home page: the legal links sit in the bottom row beside the copyright (the
   designer's footer has no counterpart, and dropping them would take them off
   every page), and the Shop column's product hrefs point at this app's real
   routes rather than the designer's `/products`. */
const LEGAL_LINKS = [
  { label: 'Shipping and Returns', href: 'https://legal.shopdotapp.com/shipping-and-returns-policy/' },
  { label: 'Terms of Use', href: 'https://legal.shopdotapp.com/retail-terms/' },
  { label: 'Privacy Policy', href: 'https://legal.shopdotapp.com/privacy-policy/' },
];

const Footer = () => {
  const { storeDetails, pageDetails } = useStore();

  useEffect(() => {
    if (storeDetails?.store_name) {
      document.title = storeDetails?.store_name + ` 's Online Store`;
    }
  }, [storeDetails?.store_name]);

  if (!pageDetails?.page_data) return null;

  const pageSocial = pageDetails.page_data.socialLinks || {};
  const retailerDetails = storeDetails?.user?.retailer_details;
  const profileLinks = Array.isArray(storeDetails?.store_social_links)
    ? Object.fromEntries(storeDetails.store_social_links.filter((link) => link?.type && link.type !== 'Website' && String(link.url || '').trim()).map((link) => [link.type.toLowerCase(), link.url]))
    : null;
  const social = {
    ...pageSocial,
    ...(retailerDetails && 'business_website' in retailerDetails ? { website: retailerDetails.business_website || '' } : {}),
    ...(profileLinks
      ? {
        facebook: profileLinks.facebook || '',
        instagram: profileLinks.instagram || '',
        tiktok: profileLinks.tiktok || '',
        youtube: profileLinks.youtube || '',
      }
      : {}),
    animalList: profileLinks?.animallist || '',
  };
  const appearance = pageDetails.page_data.colorAndApperances || {};
  const storeName = storeDetails?.store_name || '';

  const html = buildFooterHtml({
    storeName,
    logoUrl: storeDetails?.store_logo || pageDetails.page_data.logoIdentity?.storeLogo,
    initials: initialsOf(storeName),
    message: social.message,
    address: {
      street: social.street,
      city: social.city,
      state: social.state,
      zip: social.zip,
    },
    phone: social.phone,
    email: social.email,
    website: social.showWebsite === false ? '' : social.website,
    showSocials: Boolean(social.showSocials),
    shopLinks: FOOTER_SHOP_LINKS,
    socials: social,
    themeColor: appearance.colorTheme || '#E15439',
    // Quoted: the stored value is a bare family name, and the ones with a space
    // in them ("General Sans") are invalid CSS unquoted.
    headingFont: appearance.font ? `'${appearance.font}', sans-serif` : '',
    legalLinks: [],
  });

  return (
    <>
      {/* Outside Helmet: a page's own Helmet styles replace this one, which left every page but home unstyled. */}
      <style>{FOOTER_STYLES}</style>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </>
  );
};

export default Footer;
